/**
 * Excel Export Utility
 * - Converts table data to Excel with proper number formatting
 * - Prioritizes HTML parsing for accurate column alignment
 */
import * as XLSX from 'xlsx';

interface Block {
  id: string;
  block_type: string;
  text?: string;
  html?: string;
  children?: Block[];
  bbox?: number[];
}

/**
 * Parse cell value - convert numeric strings to actual numbers
 * Handles: integers, decimals, percentages, currency, negatives in parentheses
 */
function parseCell(text: string): string | number {
  const cleaned = text.trim();
  if (!cleaned) return '';

  // Percentage: "2%", "-2%", "(3.5%)"
  const pctMatch = cleaned.match(/^\(?(-?[\d,]+\.?\d*)\s*%\)?$/);
  if (pctMatch) {
    const num = parseFloat(pctMatch[1].replace(/,/g, ''));
    const isNegative = cleaned.startsWith('(') || cleaned.startsWith('-');
    return (isNegative && num > 0 ? -num : num) / 100;
  }

  // Currency: "$1,234" or "($500)"
  const currMatch = cleaned.match(/^\(?-?\$([\d,]+\.?\d*)\)?$/);
  if (currMatch) {
    const num = parseFloat(currMatch[1].replace(/,/g, ''));
    return cleaned.startsWith('(') ? -num : num;
  }

  // Plain number: "8,301", "1234.56", "(500)"
  if (/^\(?([\d,]+\.?\d*)\)?$/.test(cleaned)) {
    const num = parseFloat(cleaned.replace(/[(),]/g, ''));
    return cleaned.startsWith('(') ? -num : num;
  }

  return cleaned;
}

/**
 * Extract rows from HTML table (most reliable method)
 */
function parseTableHTML(html: string): (string | number)[][] {
  const div = document.createElement('div');
  div.innerHTML = html;
  const table = div.querySelector('table');
  if (!table) return [];

  return Array.from(table.querySelectorAll('tr'))
    .map(tr => Array.from(tr.querySelectorAll('td, th'))
      .map(cell => parseCell(cell.textContent?.trim() || ''))
    )
    .filter(row => row.length > 0);
}

/**
 * Clean HTML to plain text
 */
function cleanText(str: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = str.replace(/<br\s*\/?>/gi, ' ');
  return (temp.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * Extract rows from TableRow children (fallback method)
 */
function parseTableRows(children: Block[]): (string | number)[][] {
  const tableRows = children.filter(c => c.block_type === 'TableRow');
  if (!tableRows.length) return [];

  return tableRows.map(row =>
    (row.children || [])
      .sort((a, b) => (a.bbox?.[0] || 0) - (b.bbox?.[0] || 0))
      .map(cell => parseCell(cleanText(cell.text || cell.html || '')))
  );
}

/**
 * Main export function - exports a table block to Excel
 */
export function exportTableToExcel(block: Block, filename: string): void {
  let rows: (string | number)[][] = [];

  // Priority 1: HTML table (most reliable for column alignment)
  if (block.html?.includes('<table')) {
    rows = parseTableHTML(block.html);
  }

  // Priority 2: TableRow children
  if (!rows.length && block.children) {
    rows = parseTableRows(block.children);
  }

  if (!rows.length) {
    alert('Could not extract table data');
    return;
  }

  // Create Excel worksheet
  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Auto-size columns
  const widths: number[] = [];
  rows.forEach(row => {
    row.forEach((cell, i) => {
      const len = String(cell).length + 2;
      widths[i] = Math.max(widths[i] || 8, Math.min(len, 40));
    });
  });
  ws['!cols'] = widths.map(w => ({ wch: w }));

  // Apply percentage format to cells that are decimals < 1 (likely percentages)
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      const cell = ws[ref];
      if (cell && typeof cell.v === 'number' && Math.abs(cell.v) < 1 && cell.v !== 0) {
        cell.z = '0%';  // Format as percentage
      }
    }
  }

  // Create workbook and export
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Table');
  XLSX.writeFile(wb, filename);
}
